import React, { useEffect, useState, useCallback } from 'react';
import { api } from './App';
import { Warning, ArrowRight, Close, Clock } from './Icons';
import { formatDate } from './dateUtils';
import { InfoTooltip } from './InfoTooltip';

// Topic areas with notably higher exam weighting
const HIGH_WEIGHT_AREAS = new Set(['Business Law and Practice', 'Dispute Resolution', 'Property Law and Practice', 'Wills and the Administration of Estates', 'Criminal Liability', 'Criminal Law and Practice']);

const PRIORITY_TIERS = {
  1:{label:'Not started',color:'#9ca3af',bg:'rgba(156,163,175,0.12)',stale:false,dot:false},
  2:{label:'In progress',color:'#a78bfa',bg:'rgba(167,139,250,0.12)',stale:false,dot:false},
  3:{label:'Needs work',color:'#e05555',bg:'rgba(224,85,85,0.18)',stale:true,dot:true},
  4:{label:'Needs work',color:'#f87171',bg:'rgba(248,113,113,0.09)',stale:false,dot:true},
  5:{label:'Moderate',color:'#d4903a',bg:'rgba(212,144,58,0.16)',stale:true,dot:false},
  6:{label:'Moderate',color:'#fbbf24',bg:'rgba(251,191,36,0.08)',stale:false,dot:false},
  7:{label:'Strong',color:'#4caf7d',bg:'rgba(76,175,125,0.10)',stale:true,dot:false},
  8:{label:'Strong',color:'#86efac',bg:'rgba(134,239,172,0.06)',stale:false,dot:false},
};

function daysAgo(s){if(!s)return 999;for(const f of[s=>{const[d,m,y]=s.split('.');return new Date(y.length===2?`20${y}`:y,m-1,d);},s=>new Date(s)]){try{const d=f(s);if(!isNaN(d))return Math.floor((Date.now()-d)/86400000);}catch{}}return 999;}

function getPriorityTier(t,red=0.4,green=0.7){const c=t.confidence;const st=t.status||'';const r=daysAgo(t.date_updated)<=7;if(c===null&&!st)return 1;if(c===null&&st)return 2;if(c<red&&!r)return 3;if(c<red&&r)return 4;if(c<green&&!r)return 5;if(c<green&&r)return 6;if(c>=green&&!r)return 7;return 8;}

function TierBadge({tier}){const t=PRIORITY_TIERS[tier];if(!t)return null;return(<span className="tier-badge" style={{color:t.color,background:t.bg,borderColor:t.color+'55'}}>{t.dot&&<span className="tier-dot" style={{background:t.color}}/>}{t.label}{t.stale&&<span className="tier-stale-tag">stale</span>}</span>);}

function confColor(c,red=0.4,green=0.7){if(c===null||c===undefined)return 'var(--muted)';if(c<red)return 'var(--red)';if(c<green)return 'var(--amber)';return 'var(--green)';}

function ReadinessBands({ flk1Bands, flk2Bands }) {
  if (!flk1Bands && !flk2Bands) return null;
  const modules = [
    { label: 'FLK1', bands: flk1Bands, accent: 'var(--flk1)' },
    { label: 'FLK2', bands: flk2Bands, accent: 'var(--flk2)' },
  ];
  return (
    <div className="readiness-bands-panel">
      {modules.map(({ label, bands, accent }) => {
        if (!bands) return null;
        const { green_pct, amber_pct, red_pct, unrated_pct } = bands;
        return (
          <div key={label} className="readiness-bands-row">
            <div className="readiness-bands-label" style={{ color: accent }}>{label}</div>
            <div className="readiness-bands-bar">
              <div style={{ width: green_pct + '%', background: '#22C55E' }} />
              <div style={{ width: amber_pct + '%', background: '#F59E0B' }} />
              <div style={{ width: red_pct + '%', background: '#EF4444' }} />
              <div style={{ width: unrated_pct + '%', background: '#e5e7eb' }} />
            </div>
            <div className="readiness-bands-labels">
              <span style={{ color: '#166534' }}>{Math.round(green_pct)}% green</span>
              <span style={{ color: '#92400E' }}>{Math.round(amber_pct)}% amber</span>
              <span style={{ color: '#991B1B' }}>{Math.round(red_pct)}% red</span>
              {unrated_pct > 0 && <span style={{ color: 'var(--muted)' }}>{Math.round(unrated_pct)}% unrated</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExamCountdown({label,dateKey,dateVal,onSave,accent}){
  const[editing,setEditing]=useState(false);const[draft,setDraft]=useState(dateVal||'');
  useEffect(()=>{setDraft(dateVal||'');},[dateVal]);
  const save=()=>{onSave(dateKey,draft);setEditing(false);};
  if(!dateVal||editing)return(<div className="exam-card" style={{borderColor:accent}}><div className="exam-card-label" style={{color:accent}}>{label}</div><div style={{display:'flex',gap:8,alignItems:'center',marginTop:8}}><input className="exam-input" type="date" value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={e=>e.key==='Enter'&&save()}/><button className="countdown-edit" onClick={save}>Save</button></div></div>);
  const exam=new Date(dateVal);const daysLeft=Math.ceil((exam-Date.now())/86400000);const urgency=daysLeft<14?'var(--red)':daysLeft<28?'var(--amber)':accent;
  return(<div className="exam-card" style={{borderColor:accent}}><div className="exam-card-label" style={{color:accent}}>{label}</div><div className="exam-card-days" style={{color:urgency}}>{daysLeft}</div><div className="exam-card-sub">days left · {Math.floor(daysLeft/7)} weeks</div><div className="exam-card-date">{formatDate(exam, 'weekday')}</div><button className="countdown-edit" onClick={()=>setEditing(true)} style={{marginTop:6}}>Edit</button></div>);
}

function CalibrationAlerts({alerts,onTabChange,thRed=0.4,thGreen=0.7}){
  if(!alerts||alerts.length===0)return null;
  return(
    <div className="calibration-panel">
      <div className="calibration-header">
        <span className="calibration-title"><Warning size={14} style={{marginRight:4,verticalAlign:'middle'}} /> Confidence vs MCQ accuracy: gaps detected</span>
        <span className="calibration-sub">Your self-rating is higher than MCQ results on these topics</span>
      </div>
      <div className="calibration-list">
        {alerts.map(t=>(
          <div key={t.id} className="calibration-row" onClick={()=>onTabChange(t.module==='FLK1'?'flk1':'flk2')}>
            <div className="calibration-topic">
              <span className="calibration-name">{t.subtopic}</span>
              <span className="calibration-meta">{t.module} · {t.topic}</span>
            </div>
            <div className="calibration-scores">
              <div className="calibration-score"><span className="calibration-score-label">Self</span><span className="calibration-score-val" style={{color:confColor(t.confidence,thRed,thGreen)}}>{Math.round(t.confidence*100)}%</span></div>
              <span className="calibration-arrow"><ArrowRight size={12} /></span>
              <div className="calibration-score"><span className="calibration-score-label">MCQ</span><span className="calibration-score-val" style={{color:confColor(t.mcq_accuracy,thRed,thGreen)}}>{Math.round(t.mcq_accuracy*100)}%</span></div>
              <span className="calibration-gap">−{t.gap}%</span>
            </div>
          </div>
        ))}
      </div>
      <div className="calibration-note">Click any row to review the topic. Consider lowering your confidence rating to match MCQ performance.</div>
    </div>
  );
}

function PriorityList({topics,moduleColor,onTabChange,module}){
  if(!topics||topics.length===0)return<div className="priority-list"><div className="priority-empty">No topics need attention</div></div>;
  return(
    <div className="priority-list" style={{borderLeftColor:moduleColor}}>
      {topics.map(t=>{const tier=getPriorityTier(t);const info=PRIORITY_TIERS[tier];return(
        <div className="priority-row" key={t.id} onClick={()=>onTabChange(module)}>
          <div className="priority-tier-stripe" style={{background:info?.color,opacity:info?.stale?0.7:0.25}}/>
          <div className="priority-text"><span className="priority-sub">{t.subtopic}</span><span className="priority-meta">{t.topic}</span></div>
          <TierBadge tier={tier}/>
          {t.mcq_accuracy!=null&&(<span className="mcq-acc-badge" style={{color:t.mcq_accuracy<0.5?'var(--red)':t.mcq_accuracy>=0.7?'var(--green)':'var(--amber)',background:t.mcq_accuracy<0.5?'rgba(224,85,85,0.1)':t.mcq_accuracy>=0.7?'rgba(76,175,125,0.1)':'rgba(212,144,58,0.1)',borderColor:t.mcq_accuracy<0.5?'rgba(224,85,85,0.3)':t.mcq_accuracy>=0.7?'rgba(76,175,125,0.3)':'rgba(212,144,58,0.3)'}} title="MCQ accuracy">MCQ {Math.round(t.mcq_accuracy*100)}%</span>)}
          {t.sra_accuracy!=null&&(<span className="mcq-acc-badge" style={{color:t.sra_accuracy<0.5?'var(--red)':t.sra_accuracy>=0.7?'var(--green)':'var(--amber)',background:t.sra_accuracy<0.5?'rgba(224,85,85,0.1)':t.sra_accuracy>=0.7?'rgba(76,175,125,0.1)':'rgba(212,144,58,0.1)',borderColor:t.sra_accuracy<0.5?'rgba(224,85,85,0.3)':t.sra_accuracy>=0.7?'rgba(76,175,125,0.3)':'rgba(212,144,58,0.3)'}} title="SRA questions accuracy">SRA {Math.round(t.sra_accuracy*100)}%</span>)}
          {HIGH_WEIGHT_AREAS.has(t.topic)&&(<span className="high-weight-tag" title="Higher exam weighting — prioritise this area">High weight</span>)}
          <span className="priority-conf" style={{color:confColor(t.confidence)}}>{t.confidence!==null?Math.round(t.confidence*100)+'%':'—'}</span>
        </div>
      );})}
    </div>
  );
}

function LastTouchedPanel({topics,module,moduleColor,onConfidenceChange,thRed=0.4,thGreen=0.7}){
  const[updating,setUpdating]=useState(null);const[confValues,setConfValues]=useState({});
  useEffect(()=>{const v={};topics.forEach(t=>{if(t.confidence!==null)v[t.id]=Math.round(t.confidence*100);});setConfValues(v);},[topics]);
  const handleConfChange=async(topic,newConf)=>{
    const confNum=newConf/100;setUpdating(topic.id);
    await api(`/api/topics/${topic.id}`,{method:'PATCH',body:JSON.stringify({confidence:confNum,status:topic.status||'',date_updated:new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}).replace(/\//g,'.')})});
    if(topic.confidence!==null&&confNum<topic.confidence-0.1){await api('/api/alerts/create',{method:'POST',body:JSON.stringify({message:`${topic.subtopic} confidence dropped to ${newConf}%. Consider adding a planner slot.`,topic_id:topic.id})});}
    setUpdating(null);onConfidenceChange&&onConfidenceChange(topic.id,confNum);
  };
  if(!topics||topics.length===0)return null;
  return(
    <div className="last-touched-panel">
      <div className="last-touched-title"><span style={{color:moduleColor}}>{module}</span>: topics to revisit<span className="last-touched-hint">Adjust if your confidence has changed since you last revised</span></div>
      {topics.map(t=>{
        const staleness=daysAgo(t.date_updated);const staleText=staleness===999?'never rated':staleness===0?'today':staleness===1?'yesterday':`${staleness}d ago`;const isStale=staleness>7;const tier=getPriorityTier(t,thRed,thGreen);const currentConf=confValues[t.id]??(t.confidence!==null?Math.round(t.confidence*100):null);
        return(<div key={t.id} className="touched-row"><div className="touched-left"><div className="touched-name">{t.subtopic}</div><div className="touched-meta"><span className="touched-when" style={{color:isStale?'var(--red)':'var(--muted)'}}>{isStale&&<Warning size={11} style={{marginRight:2,verticalAlign:'middle'}} />}{isStale&&' '}{staleText}</span><TierBadge tier={tier}/></div></div><div className="touched-right">{currentConf!==null?(<><span className="touched-conf" style={{color:confColor(currentConf/100,thRed,thGreen)}}>{currentConf}%</span><input type="range" min="0" max="100" step="5" value={currentConf} className="touched-slider" onChange={e=>setConfValues(prev=>({...prev,[t.id]:parseInt(e.target.value)}))} onMouseUp={e=>handleConfChange(t,parseInt(e.target.value))} onTouchEnd={e=>handleConfChange(t,parseInt(e.currentTarget.value))}/>{updating===t.id&&<span className="touched-saving">saving</span>}</>):(<span style={{fontSize:11,color:'var(--hint)'}}>not rated</span>)}</div></div>);
      })}
    </div>
  );
}

const MCQ_MILESTONES=[500,1000,1500,2000,2500,3000,3700];
const MCQ_MESSAGES={
  0:'Log your MCQ sessions here. Per-topic accuracy feeds the priority dashboard.',
  500:'500 questions logged. Your per-topic accuracy baseline is forming.',
  1000:'1,000 questions logged. Accuracy patterns are visible across your weakest topics.',
  1500:'1,500 questions logged. Recall is stronger across topics you have repeated.',
  2000:'2,000 questions logged. Focus remaining sessions on topics where accuracy is lowest.',
  2500:'2,500 questions logged. Concentrate on weak topics and maintain your rhythm.',
  3000:'3,000 questions logged. Focus remaining sessions on topics where accuracy is still below your target.',
};

function MCQMilestone({mcqTotal,mcqTarget,confidenceTarget}){
  const next=MCQ_MILESTONES.find(m=>m>mcqTotal)||3700;
  const prev=MCQ_MILESTONES.slice().reverse().find(m=>m<=mcqTotal)||0;
  const pct=prev===next?100:Math.round(((mcqTotal-prev)/(next-prev))*100);
  const confPct=Math.round((parseFloat(confidenceTarget)||0.70)*100);
  const messages={...MCQ_MESSAGES,3000:`3,000 questions logged. Focus remaining sessions on topics where accuracy is still below ${confPct}%.`};
  const msg=messages[prev]||messages[3000];
  const target=parseInt(mcqTarget)||40;
  return(
    <div className="mcq-milestone">
      <div className="mcq-milestone-header">
        <div>
          <span className="mcq-milestone-count">{mcqTotal.toLocaleString()}</span>
          <span className="mcq-milestone-label"> MCQs logged</span>
          {mcqTotal<3700&&<span className="mcq-milestone-next"> · next milestone: {next.toLocaleString()}</span>}
        </div>
        <span className="mcq-milestone-target">Daily target: {target} <InfoTooltip text="Your target number of MCQ questions per day. Set in Settings. The weekly and overall targets are calculated from this." /></span>
      </div>
      {mcqTotal<3700&&(
        <div className="mcq-milestone-bar-bg">
          <div className="mcq-milestone-bar-fill" style={{width:pct+'%'}}/>
        </div>
      )}
      <p className="mcq-milestone-msg">{msg}</p>
    </div>
  );
}


const REVISION_PRINCIPLES = [
  {
    title: 'Retrieval beats re-reading',
    text: 'Writing down everything you remember before checking your notes builds far stronger memories than reading the material again. The struggle is the learning.',
  },
  {
    title: 'Stale topics decay faster than you think',
    text: 'Topics you last revised more than 7 days ago have faded more than you expect. Prioritise stale topics over recent ones, even if they feel comfortable.',
  },
  {
    title: 'Interleave, don\'t block',
    text: 'Mixing different topics in a single session — rather than spending an hour on one topic — feels harder but produces better long-term retention.',
  },
  {
    title: 'Calibrate your confidence with honesty',
    text: 'If your MCQ accuracy is lower than your self-rating on a topic across multiple sessions, your confidence rating is inflated. Adjust it down to reflect your performance under test conditions.',
  },
  {
    title: 'Space your review at increasing intervals',
    text: 'Reviewing a topic at 1 day, then 3 days, then 7 days creates far stronger memories than reviewing it three times in one week. Let your brain almost forget before you review.',
  },
  {
    title: 'Ask why, not just what',
    text: 'Elaborative interrogation — asking yourself why a rule exists and how it connects to other rules — builds richer understanding that holds up under novel exam questions.',
  },
  {
    title: 'Weak topics deserve more repetitions',
    text: 'It takes more repetitions to reach the same retention level for difficult material. Don\'t spend equal time on all topics — weight your effort toward your weakest areas.',
  },
];

function RevisionPrinciple() {
  const idx = new Date().getDay(); // 0–6
  const p = REVISION_PRINCIPLES[idx % REVISION_PRINCIPLES.length];
  return (
    <div className="science-box" style={{marginTop:16}}>
      <div className="science-title">Revision principle</div>
      <p style={{fontWeight:600,marginBottom:4,fontSize:13}}>{p.title}</p>
      <p style={{marginTop:0}}>{p.text}</p>
    </div>
  );
}

function StartHereCard({ onTabChange }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '20px 24px',
      marginBottom: 16,
    }}>
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16, color: 'var(--text)' }}>
        Start here
      </div>
      {[
        {
          n: 1,
          heading: 'Rate your confidence',
          link: 'Open FLK1 or FLK2',
          tab: 'flk1',
          body: ' and rate 10–15 topics you feel strongest and weakest on. Your priority dashboard builds from these ratings.',
        },
        {
          n: 2,
          heading: 'Import your scores',
          link: 'How to import Law Drills results',
          href: '/help/import-law-drills',
          body: ' — use the SQETrack Chrome extension to pull in your Law Drills results.',
        },
        {
          n: 3,
          heading: 'Try the SRA questions',
          link: 'Open SRA Questions',
          tab: 'sra',
          body: ' — 220 official sample questions mapped to your FLK topics. Your scores feed the dashboard.',
        },
      ].map(({ n, heading, link, tab, href, body }) => (
        <div key={n} style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-start' }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: '#3B82F6', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1,
          }}>
            {n}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            <span style={{ fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 2 }}>
              {heading}
            </span>
            {href ? (
              <a
                href={href}
                style={{
                  color: '#3B82F6', fontSize: 13,
                  textDecoration: 'underline',
                }}
              >
                {link}
              </a>
            ) : (
              <button
                onClick={() => onTabChange(tab)}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#3B82F6', cursor: 'pointer', fontSize: 13,
                  textDecoration: 'underline',
                }}
              >
                {link}
              </button>
            )}
            {body}
          </div>
        </div>
      ))}
    </div>
  );
}

function L2PriorityList({ topics, moduleColor, onTabChange, module, thRed=0.4, thGreen=0.7 }) {
  if (!topics || topics.length === 0) {
    return <div className="priority-list"><div className="priority-empty">No topics need attention</div></div>;
  }
  return (
    <div className="priority-list" style={{ borderLeftColor: moduleColor }}>
      {topics.map((t, i) => (
        <div className="priority-row" key={`${t.general_topic}-${i}`}
          onClick={() => onTabChange(module)}>
          <div className="priority-text">
            <span className="priority-sub">{t.general_topic}</span>
            <span className="priority-meta">{t.subject_area} · {t.rated_count} of {t.subtopic_count} topics rated</span>
          </div>
          <span className="priority-conf" style={{ color: confColor(t.avg_confidence, thRed, thGreen) }}>
            {t.avg_confidence !== null ? Math.round(t.avg_confidence * 100) + '%' : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard({onTabChange}){
  const[stats,setStats]=useState(null);const[settings,setSettings]=useState({});const[checkins,setCheckins]=useState([]);const[dismissedCheckin,setDismissedCheckin]=useState(false);const[dismissedFbPrompt,setDismissedFbPrompt]=useState(false);

  useEffect(()=>{api('/api/stats').then(setStats);api('/api/settings').then(setSettings);api('/api/checkin-suggestions').then(setCheckins);},[]);

  const saveSetting=async(key,value)=>{setSettings(prev=>({...prev,[key]:value}));await api('/api/settings',{method:'POST',body:JSON.stringify({[key]:value})});};

  const handleConfidenceChange=useCallback((topicId,newConf)=>{setStats(prev=>{if(!prev)return prev;const upd=arr=>arr.map(t=>t.id===topicId?{...t,confidence:newConf}:t);return{...prev,flk1_touched:upd(prev.flk1_touched||[]),flk2_touched:upd(prev.flk2_touched||[]),flk1_priority:upd(prev.flk1_priority||[]),flk2_priority:upd(prev.flk2_priority||[])};});},[]);

  if(!stats)return<div className="loading">Loading</div>;
  const pctStarted=stats.l2_total?Math.round(((stats.l2_total-(stats.l2_untouched||0))/stats.l2_total)*100):0;const mcqPct=Math.min(100,Math.round((stats.mcq_total/3700)*100));const show=key=>settings[key]!=='0';
  const thRed=parseFloat(settings.confidence_threshold_red)||0.4;const thGreen=parseFloat(settings.confidence_threshold_green)||0.7;
  const studyStart=settings.study_start?new Date(settings.study_start):null;
  const daysStudying=studyStart?Math.floor((Date.now()-studyStart)/86400000):0;
  const showFbPrompt=daysStudying>=7&&settings.feedback_prompt_shown!=='1'&&!dismissedFbPrompt;
  const dismissFbPrompt=(goToFeedback)=>{setDismissedFbPrompt(true);saveSetting('feedback_prompt_shown','1');if(goToFeedback)onTabChange('feedback');};

  return(
    <div className="dashboard">
      <div className="exam-dates-row">
        <ExamCountdown label="FLK1 exam" dateKey="flk1_exam_date" dateVal={settings.flk1_exam_date} onSave={saveSetting} accent="var(--flk1)"/>
        <ExamCountdown label="FLK2 exam" dateKey="flk2_exam_date" dateVal={settings.flk2_exam_date} onSave={saveSetting} accent="var(--flk2)"/>
      </div>

      <ReadinessBands
        flk1Bands={stats.flk1_bands}
        flk2Bands={stats.flk2_bands}
      />

      <div className={settings.dashboard_metric_layout === 'grouped' ? 'metric-row-grouped' : 'metric-row'}>
        {settings.dashboard_metric_layout === 'grouped' ? (
          <>
            <div className="metric-row-grouped-top">
              <div className="metric"><span className="metric-val" style={{color:'var(--red)'}}>{stats.l2_weak||0}</span><span className="metric-label">needs work <span className="metric-hint">(&lt;40%)</span><InfoTooltip text="Topics rated below your needs-work threshold. Adjust the threshold in Settings." /></span></div>
              <div className="metric"><span className="metric-val" style={{color:'var(--amber)'}}>{stats.l2_moderate||0}</span><span className="metric-label">moderate <span className="metric-hint">(40-69%)</span><InfoTooltip text="Topics rated between your needs-work and strong thresholds." /></span></div>
              <div className="metric"><span className="metric-val" style={{color:'var(--green)'}}>{stats.l2_strong||0}</span><span className="metric-label">strong <span className="metric-hint">(70%+)</span><InfoTooltip text="Topics rated above your strong threshold. Adjust in Settings." /></span></div>
            </div>
            <div className="metric-row-grouped-bottom">
              <div className="metric"><span className="metric-val">{(stats.l2_total||0)-(stats.l2_untouched||0)}<span className="metric-denom"> of {stats.l2_total||0}</span></span><span className="metric-label">topics rated <InfoTooltip text="The number of topics where you have set a confidence rating. Counts across FLK1 and FLK2." /></span><div className="mini-bar-bg"><div className="mini-bar-fill" style={{width:(stats.l2_total?Math.round(((stats.l2_total-(stats.l2_untouched||0))/stats.l2_total)*100):0)+'%',background:'var(--accent)'}}/></div></div>
              <div className="metric"><span className="metric-val">{stats.mcq_total.toLocaleString()}</span><span className="metric-label">MCQs logged <InfoTooltip text="Total MCQ questions logged across manual entry and imports." /></span><div className="mini-bar-bg"><div className="mini-bar-fill" style={{width:mcqPct+'%',background:'var(--accent)'}}/></div><span className="metric-hint" title="Based on your daily target over your remaining study weeks">target: {(3700).toLocaleString()}</span></div>
            </div>
          </>
        ) : (
          <>
            <div className="metric"><span className="metric-val">{(stats.l2_total||0)-(stats.l2_untouched||0)}<span className="metric-denom"> of {stats.l2_total||0}</span></span><span className="metric-label">topics rated <InfoTooltip text="The number of topics where you have set a confidence rating. Counts across FLK1 and FLK2." /></span><div className="mini-bar-bg"><div className="mini-bar-fill" style={{width:(stats.l2_total?Math.round(((stats.l2_total-(stats.l2_untouched||0))/stats.l2_total)*100):0)+'%',background:'var(--accent)'}}/></div></div>
            <div className="metric"><span className="metric-val" style={{color:'var(--red)'}}>{stats.l2_weak||0}</span><span className="metric-label">needs work <span className="metric-hint">(&lt;40%)</span><InfoTooltip text="Topics rated below your needs-work threshold. Adjust the threshold in Settings." /></span></div>
            <div className="metric"><span className="metric-val" style={{color:'var(--amber)'}}>{stats.l2_moderate||0}</span><span className="metric-label">moderate <span className="metric-hint">(40-69%)</span><InfoTooltip text="Topics rated between your needs-work and strong thresholds." /></span></div>
            <div className="metric"><span className="metric-val" style={{color:'var(--green)'}}>{stats.l2_strong||0}</span><span className="metric-label">strong <span className="metric-hint">(70%+)</span><InfoTooltip text="Topics rated above your strong threshold. Adjust in Settings." /></span></div>
            <div className="metric"><span className="metric-val">{stats.mcq_total.toLocaleString()}</span><span className="metric-label">MCQs logged <InfoTooltip text="Total MCQ questions logged across manual entry and imports." /></span><div className="mini-bar-bg"><div className="mini-bar-fill" style={{width:mcqPct+'%',background:'var(--accent)'}}/></div><span className="metric-hint" title="Based on your daily target over your remaining study weeks">target: {(3700).toLocaleString()}</span></div>
          </>
        )}
      </div>

      {((stats.untouched_flk1 || 0) > 0 || (stats.untouched_flk2 || 0) > 0) && (
        <div className="untouched-notice">
          Topics not yet studied:
          {(stats.untouched_flk1 || 0) > 0 && <span className="untouched-module-tag untouched-flk1">{stats.untouched_flk1} FLK1</span>}
          {(stats.untouched_flk2 || 0) > 0 && <span className="untouched-module-tag untouched-flk2">{stats.untouched_flk2} FLK2</span>}
        </div>
      )}

      {show('dash_show_calibration')&&stats.calibration_alerts?.length>0&&<CalibrationAlerts alerts={stats.calibration_alerts} onTabChange={onTabChange} thRed={thRed} thGreen={thGreen}/>}

      {show('dash_show_legend')&&(
        <div className="priority-legend">
          <span className="priority-legend-label">Priority:</span>
          {[{color:'#e05555',label:'Needs work (stale)',stale:true},{color:'#f87171',label:'Needs work (recent)',stale:false},{color:'#d4903a',label:'Moderate (stale)',stale:true},{color:'#fbbf24',label:'Moderate (recent)',stale:false},{color:'#4caf7d',label:'Strong (stale)',stale:true},{color:'#9ca3af',label:'Not started',stale:false}].map(item=>(
            <span key={item.label} className="priority-legend-item"><span className="priority-legend-swatch" style={{background:item.color,opacity:item.stale?1:0.5}}/><span style={{color:'var(--muted)',fontSize:10}}>{item.label}</span></span>
          ))}
          <span style={{fontSize:10,color:'var(--hint)',marginLeft:4}}>Stale = 7+ days</span>
        </div>
      )}

      {show('dash_show_priority') && (
        stats.started < 5
          ? <StartHereCard onTabChange={onTabChange} />
          : (
              <div className="dash-cols">
                <div className="dash-col"><div className="section-title" style={{color:'var(--flk1)'}}>FLK1 focus topics <InfoTooltip text="Your lowest-confidence topics in each module, ranked by priority. Priority factors in confidence level, MCQ accuracy, exam weighting, and how recently you reviewed the topic." /></div><L2PriorityList topics={stats.flk1_l2_priority} moduleColor="var(--flk1)" onTabChange={onTabChange} module="flk1" thRed={thRed} thGreen={thGreen}/></div>
                <div className="dash-col"><div className="section-title" style={{color:'var(--flk2)'}}>FLK2 focus topics <InfoTooltip text="Your lowest-confidence topics in each module, ranked by priority. Priority factors in confidence level, MCQ accuracy, exam weighting, and how recently you reviewed the topic." /></div><L2PriorityList topics={stats.flk2_l2_priority} moduleColor="var(--flk2)" onTabChange={onTabChange} module="flk2" thRed={thRed} thGreen={thGreen}/></div>
              </div>
            )
      )}

      {show('dash_show_mcq')&&<MCQMilestone mcqTotal={stats.mcq_total} mcqTarget={settings.mcq_daily_target} confidenceTarget={settings.target_confidence}/>}

      {show('dash_show_checkin')&&checkins.length>0&&!dismissedCheckin&&(
        <div className="checkin-banner" style={{marginTop:16}}>
          <div className="checkin-header"><span className="checkin-title"><Clock size={14} /> Time for a quick recall check <InfoTooltip text="Topics you have not reviewed in 7 or more days. Retention research shows recall drops off after about two weeks without reinforcement." /></span><button className="checkin-dismiss" onClick={()=>setDismissedCheckin(true)}><Close size={12} /></button></div>
          <p className="checkin-desc">These topics have not been recalled in the past 7 days. A 3-minute session now is more effective than re-reading.</p>
          <div className="checkin-topics">{checkins.slice(0,4).map(t=>(<button key={t.id} className="checkin-topic-btn" onClick={()=>onTabChange('recall')}><span className="checkin-topic-name">{t.subtopic}</span><span className="checkin-topic-meta">{t.last_recall?`Last recalled ${t.days_since_recall}d ago`:'Never recalled'}{' · '}{t.confidence!==null?Math.round(t.confidence*100)+'%':'not rated'}</span></button>))}</div>
          <button className="checkin-go-btn" onClick={()=>onTabChange('recall')}>Start recall session <ArrowRight size={14} /></button>
        </div>
      )}

      {show('dash_show_touched')&&(stats.flk1_touched?.length>0||stats.flk2_touched?.length>0)&&(
        <div className="dash-cols" style={{marginTop:16}}>
          <LastTouchedPanel topics={stats.flk1_touched||[]} module="FLK1" moduleColor="var(--flk1)" onConfidenceChange={handleConfidenceChange} thRed={thRed} thGreen={thGreen}/>
          <LastTouchedPanel topics={stats.flk2_touched||[]} module="FLK2" moduleColor="var(--flk2)" onConfidenceChange={handleConfidenceChange} thRed={thRed} thGreen={thGreen}/>
        </div>
      )}

      {/* ── Exam readiness tracking panel ── */}
      {stats.below_target !== undefined && stats.started > 0 && (
        <div className="readiness-panel">
          <div className="readiness-header">
            <span className="readiness-title">Exam readiness <InfoTooltip text="Blends your average confidence (40% weight) with your latest practice exam score (60% weight). Without practice exam data, uses confidence alone." /></span>
            <span className="readiness-sub">{Math.round((stats.target_conf || 0.70) * 100)}% target</span>
          </div>
          <div className="readiness-body">
            {(() => {
              const bt = stats.below_target;
              const scored = stats.started;
              const total = stats.total;
              const pct = scored > 0 ? Math.round((1 - bt / scored) * 100) : 0;
              const badge = pct >= 80 ? 'green' : pct >= 50 ? 'amber' : 'red';
              const badgeColor = badge === 'green' ? 'var(--green)' : badge === 'amber' ? 'var(--amber)' : 'var(--red)';
              const badgeBg = badge === 'green' ? 'rgba(76,175,125,0.12)' : badge === 'amber' ? 'rgba(212,144,58,0.12)' : 'rgba(224,85,85,0.12)';
              return (
                <>
                  <div className="readiness-count">
                    <span className="readiness-num" style={{ color: bt === 0 ? 'var(--green)' : 'var(--text)' }}>{bt}</span>
                    <span className="readiness-label"> topic{bt !== 1 ? 's' : ''} still below target</span>
                    <span className="readiness-badge" style={{ color: badgeColor, background: badgeBg, border: `1px solid ${badgeColor}55` }}>
                      {pct}% of topics at target
                    </span>
                  </div>
                  {bt === 0 && scored >= 10 ? (
                    <p className="readiness-msg" style={{ color: 'var(--green)' }}>All rated topics are at or above your target. Stale topics will decay without review.</p>
                  ) : (
                    <p className="readiness-msg">{bt} of your {scored} rated topics are below {Math.round((stats.target_conf || 0.70) * 100)}%. Focus on your priority list to close the gap. {total - scored > 0 ? `${total - scored} topics still unrated.` : ''}</p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {show('dash_show_principle')&&(
        <RevisionPrinciple />
      )}

      {showFbPrompt&&(
        <div className="fb-prompt-banner">
          <div className="fb-prompt-body">
            <div className="fb-prompt-title">How's SQETrack working for you?</div>
            <p className="fb-prompt-text">You have been studying for over a week. What is working and what could be better? Takes 30 seconds.</p>
          </div>
          <div className="fb-prompt-actions">
            <button className="fb-prompt-btn" onClick={()=>dismissFbPrompt(true)}>Give feedback</button>
            <button className="fb-prompt-dismiss" onClick={()=>dismissFbPrompt(false)}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}
